import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Alert, Spinner } from 'reactstrap';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { DateRangeSelectorState } from '../../components/DateRangeSelector';
import { getGoogleReviewAnalyticsAsync } from '../../ajax/requests';
import { GoogleReviewAnalyticsData, GoogleReviewDimensionMetric } from '../../models/api-models';
import { DimensionMetrics } from './DimensionMetrics';
import { useSelector } from 'react-redux';
import { getGeneralSettings } from '../../store/settings/selectors';
import { ComboField } from '../../components/Forms/fields';

interface AnalyticsGoogleReviewsProps {
    window: DateRangeSelectorState;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--spacer-S);
`;

const SummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
    padding: var(--spacer-S) var(--spacer-M);
    border-radius: 12px;
    border: var(--border-thin) solid var(--appLigthGrey);
    background: linear-gradient(180deg, var(--bg-light), white);
`;

const SummaryLabel = styled.span`
    color: var(--text-muted);
    font-size: 0.9rem;
    font-weight: 600;
`;

const SummaryValueRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--spacer-S);
`;

const SummaryCount = styled.span`
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--brand-secondary);
    line-height: 1;
`;

const SummaryPercent = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-muted);
`;

const SectionCard = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 12px;
    background: white;
    overflow: hidden;
`;

const SectionHeader = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    background: var(--bg-light);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    font-weight: 700;
    color: var(--text-body);
`;

const SectionBody = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const StarRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const StarLabel = styled.span`
    flex: 0 0 36px;
    font-weight: 700;
    color: var(--text-body);
`;

const StarTrack = styled.div`
    flex: 1;
    height: 10px;
    border-radius: 999px;
    background: var(--appLigthGrey);
    overflow: hidden;
`;

const StarFill = styled.div<{ $pct: number }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--brand-primary), var(--brand-secondary));
    transition: width 0.5s ease;
`;

const StarCount = styled.span`
    flex: 0 0 2.5rem;
    text-align: right;
    font-weight: 700;
    color: var(--brand-secondary);
`;

const AverageBadge = styled.div`
    display: inline-flex;
    align-items: baseline;
    gap: 0.3rem;
    padding: var(--spacer-XXS) var(--spacer-S);
    border-radius: 999px;
    background: var(--brand-secondary);
    color: white;
    font-weight: 700;
`;

const AverageValue = styled.span`
    font-size: 1.1rem;
    line-height: 1;
`;

const AverageLabel = styled.span`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const CommentsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--spacer-S);
`;

const CommentPanel = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 8px;
    background: var(--bg-light);
    overflow: hidden;
`;

const CommentPanelHeader = styled.div`
    padding: var(--spacer-XS) var(--spacer-S);
    font-weight: 700;
    background: white;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
`;

const CommentList = styled.div`
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
    padding: var(--spacer-S);
`;

const CommentItem = styled.div`
    padding: var(--spacer-XS) var(--spacer-S);
    border-radius: 8px;
    background: white;
    border: var(--border-thin) solid var(--appLigthGrey);
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
`;

const LoadingContainer = styled.div`
    text-align: center;
    padding: var(--spacer-M);
`;

const EmptyState = styled.div`
    color: var(--text-muted);
    font-style: italic;
`;

const toMetricView = (metric: GoogleReviewDimensionMetric) => ({
    id: metric.id,
    label: metric.label,
    average: metric.averageStars ?? 0,
    totalResponses: metric.totalSubmitted,
});

const formatPercent = (count: number, total: number) => `${total > 0 ? Math.round((count / total) * 100) : 0}%`;

const SectionTitle: FC<{ title: string }> = ({ title }) => <SectionHeader>{title}</SectionHeader>;

export const AnalyticsGoogleReviews: FC<AnalyticsGoogleReviewsProps> = ({ window }) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);
    const [analytics, setAnalytics] = useState<GoogleReviewAnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [branchFilter, setBranchFilter] = useState<string | undefined>(undefined);

    const branches = useMemo(() => {
        const b = generalSettings?.branches ?? {};
        return Object.entries(b).map(([id, s]) => ({ id, displayName: s.displayName }));
    }, [generalSettings?.branches]);
    const isMultiBranch = branches.length > 1;

    useEffect(() => {
        setLoading(true);
        setError(false);
        getGoogleReviewAnalyticsAsync({
            from: window.from,
            to: window.to,
            branchId: isMultiBranch ? branchFilter : undefined,
        })
            .then(data => setAnalytics(data))
            .catch(() => {
                setAnalytics(null);
                setError(true);
            })
            .finally(() => setLoading(false));
    }, [window.from, window.to, branchFilter, isMultiBranch]);

    const funnelCards = useMemo(() => {
        const totalSent = analytics?.totalSent ?? 0;
        return [
            { label: t(TK.googleReviewAnalyticsTotalSent), value: analytics?.totalSent ?? 0 },
            { label: t(TK.googleReviewAnalyticsTotalOpened), value: analytics?.totalOpened ?? 0 },
            { label: t(TK.googleReviewAnalyticsTotalSubmitted), value: analytics?.totalSubmitted ?? 0 },
            { label: t(TK.googleReviewAnalyticsTotalClickedGoogle), value: analytics?.totalClickedGoogle ?? 0 },
        ].map(card => ({
            ...card,
            percent: formatPercent(card.value, totalSent),
        }));
    }, [analytics, t]);

    const starDistribution = useMemo(() => {
        const distribution = analytics?.starDistribution ?? {};
        const total = analytics?.totalSubmitted ?? 0;
        return [5, 4, 3, 2, 1].map(star => ({
            star,
            count: (distribution as Record<string, number>)[String(star)] ?? (distribution as Record<number, number>)[star] ?? 0,
            percent: total > 0 ? Math.round((((distribution as Record<string, number>)[String(star)] ?? (distribution as Record<number, number>)[star] ?? 0) / total) * 100) : 0,
        }));
    }, [analytics]);

    const breakdownMetrics = useMemo(() => ({
        professionals: (analytics?.byProfessional ?? []).map(toMetricView),
        branches: (analytics?.byBranch ?? []).map(toMetricView),
        channels: (analytics?.byChannel ?? []).map(toMetricView),
    }), [analytics]);

    if (loading) {
        return (
            <LoadingContainer>
                <Spinner size="sm" />
            </LoadingContainer>
        );
    }

    if (error) {
        return <Alert color="danger">{t(TK.somethingWentWrong)}</Alert>;
    }

    if (!analytics) {
        return null;
    }

    return (
        <Container>
            {isMultiBranch && (
                <div style={{ alignSelf: 'flex-start', minWidth: 220 }}>
                    <ComboField
                        id="analytics-google-reviews-branch"
                        label=""
                        value={branchFilter ?? ''}
                        options={[
                            { value: '', label: t(TK.allBranches) },
                            ...branches.map(b => ({ value: b.id, label: b.displayName })),
                        ]}
                        onChange={value => setBranchFilter(value || undefined)}
                    />
                </div>
            )}
            <SummaryGrid>
                {funnelCards.map(card => (
                    <SummaryCard key={card.label}>
                        <SummaryLabel>{card.label}</SummaryLabel>
                        <SummaryValueRow>
                            <SummaryCount>{card.value}</SummaryCount>
                            <SummaryPercent>{card.percent}</SummaryPercent>
                        </SummaryValueRow>
                    </SummaryCard>
                ))}
            </SummaryGrid>

            <SectionCard>
                <SectionTitle title={t(TK.googleReviewAnalyticsStarDistribution)} />
                <SectionBody>
                    <AverageBadge>
                        <AverageValue>{(analytics.averageStars ?? 0).toFixed(1)}</AverageValue>
                        <AverageLabel>{t(TK.average)}</AverageLabel>
                    </AverageBadge>
                    {starDistribution.map(item => (
                        <StarRow key={item.star}>
                            <StarLabel>{item.star}★</StarLabel>
                            <StarTrack>
                                <StarFill $pct={item.percent} />
                            </StarTrack>
                            <StarCount>{item.count}</StarCount>
                        </StarRow>
                    ))}
                </SectionBody>
            </SectionCard>

            <CommentsGrid>
                <CommentPanel>
                    <CommentPanelHeader>{t(TK.googleReviewAnalyticsPositiveComments)}</CommentPanelHeader>
                    <CommentList>
                        {(analytics.positiveComments ?? []).length > 0
                            ? (analytics.positiveComments ?? []).map((comment, index) => <CommentItem key={`${index}-${comment}`}>{comment}</CommentItem>)
                            : <EmptyState>{t(TK.noData)}</EmptyState>}
                    </CommentList>
                </CommentPanel>
                <CommentPanel>
                    <CommentPanelHeader>{t(TK.googleReviewAnalyticsImprovementNotes)}</CommentPanelHeader>
                    <CommentList>
                        {(analytics.improvementNotes ?? []).length > 0
                            ? (analytics.improvementNotes ?? []).map((comment, index) => <CommentItem key={`${index}-${comment}`}>{comment}</CommentItem>)
                            : <EmptyState>{t(TK.noData)}</EmptyState>}
                    </CommentList>
                </CommentPanel>
            </CommentsGrid>

            <CommentsGrid>
                <DimensionMetrics title={t(TK.byProfessional)} metrics={breakdownMetrics.professionals} />
                <DimensionMetrics title={t(TK.branch)} metrics={breakdownMetrics.branches} />
                <DimensionMetrics title={t(TK.byChannel)} metrics={breakdownMetrics.channels} />
            </CommentsGrid>
        </Container>
    );
};
